import React from 'react'
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'

export type AdminPieDatum = { name: string; value: number }

type Props = {
  title: string
  desc?: string
  data: AdminPieDatum[]
  colors?: string[]
}

/** Distinct from RN app pies (yellow/blue/red) so side‑by‑side charts stay readable. */
const DEFAULT_PIE_COLORS = ['#6366f1', '#14b8a6', '#f97316', '#a855f7']

interface PieLabelProps {
  cx: number
  cy: number
  midAngle: number
  outerRadius: number
  percent: number
  payload: { value: number; name: string }
}

const buildRenderLabel =
  (totalCount: number) =>
  ({
    cx,
    cy,
    midAngle,
    outerRadius,
    percent,
    payload,
  }: PieLabelProps): React.ReactElement | null => {
    const value = payload.value
    if (!value || percent <= 0.01) {
      return null
    }

    const pctWhole =
      totalCount > 0
        ? Math.round((value / totalCount) * 100)
        : Math.round((percent ?? 0) * 100)

    const RADIAN = Math.PI / 180
    const sin = Math.sin(-midAngle * RADIAN)
    const cos = Math.cos(-midAngle * RADIAN)

    const lineLength = 20
    const sx = cx + outerRadius * cos
    const sy = cy + outerRadius * sin
    const ex = cx + (outerRadius + lineLength) * cos
    const ey = cy + (outerRadius + lineLength) * sin

    const textOffset = 4
    const textX = ex + (cos >= 0 ? textOffset : -textOffset)
    const textY = ey

    const textAnchor = cos >= 0 ? 'start' : 'end'
    const labelName =
      typeof payload.name === 'string'
        ? payload.name
        : String(payload.name ?? '')

    return (
      <>
        <polyline
          points={`${sx},${sy} ${ex},${ey}`}
          stroke="#8884d8"
          fill="none"
        />
        <text
          x={textX}
          y={textY}
          textAnchor={textAnchor}
          dominantBaseline="central"
          fill="#333"
        >
          <tspan x={textX} dy={-6} fontSize={12} fontWeight={600}>
            {`${labelName} ${value}`}
          </tspan>
          <tspan x={textX} dy={16} fontSize={11}>
            {`${pctWhole}%`}
          </tspan>
        </text>
      </>
    )
  }

const SimpleAdminPieChart: React.FC<Props> = ({
  title,
  desc,
  data,
  colors = DEFAULT_PIE_COLORS,
}) => {
  const options = data.filter(d => d.value > 0)
  const total = options.reduce((s, d) => s + d.value, 0)

  return (
    <div className="chart-container">
      <h2>{title}</h2>
      {desc ? <p>{desc}</p> : null}
      <p>Total users: {total}</p>
      {options.length === 0 ? (
        <p className="admin-chart-empty">No data</p>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={options}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={buildRenderLabel(total)}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
            >
              {options.map((entry, index) => (
                <Cell key={entry.name} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

export default SimpleAdminPieChart
